/**
 * PodiumStage — 3D iso podium for the World Cup leaderboard top-3.
 *
 * Stage layout: mascots row sits atop a row of skewed podium blocks.
 * Rank 1 (gold) is raised and centered; ranks 2 (left) and 3 (right) wear
 * a deep-purple block. Includes a Week/Overall pill toggle above the stage.
 *
 * Placeholder podium — visual will be refined once final art assets land.
 */

import React, { useMemo, useCallback } from 'react';
import { View, Text, Pressable, Platform, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { LeaderboardUserAvatar } from './LeaderboardUserAvatar';
import { getWorldCupLeaderboardStyles } from './WorldCupLeaderboard.styles';
import { getPodiumStageStyles } from './PodiumStage.styles';
import {
  LeaderboardPeriod,
  PODIUM_BLOCK_COLORS,
  PODIUM_BLOCK_HEIGHTS,
  PODIUM_COPY,
  PODIUM_PERIOD_OPTIONS,
} from './PodiumStage.constants';
import type { LeaderboardEntry, WorldCupGroupCompanyReward } from '../../../src/worldcup/types/worldcup.types';

interface PodiumStageProps {
  top3: LeaderboardEntry[];
  /** Selected leaderboard period. Required when {@link showPeriodToggle} is true. */
  period?: LeaderboardPeriod;
  onPeriodChange?: (next: LeaderboardPeriod) => void;
  /** When false (default) the Week/Overall pill is hidden — used by the global leaderboard. */
  showPeriodToggle?: boolean;
  showCurrentUserVariant?: boolean;
  /** Tap on a podium player — opens that participant's profile. */
  onPressPlayer?: (entry: LeaderboardEntry) => void;
  /** Company group rewards — when provided, shows prize image + label below each block. */
  rewards?: WorldCupGroupCompanyReward[] | null;
  testID?: string;
}

const CENTER_AVATAR_SIZE = 80;
const SIDE_AVATAR_SIZE = 64;

/**
 * Server returns entries in display order. We place them on the podium by array
 * index (gold = top3[0], silver = top3[1], bronze = top3[2]) instead of looking
 * up by rank — when multiple members tie at rank 1 (e.g. pre-kickoff, everyone
 * at 0 points), keying by rank collapses them into a single slot and the rest
 * vanish from the UI.
 */
const orderForDisplay = (top3: LeaderboardEntry[]) => {
  return [top3[1], top3[0], top3[2]] as const;
};

const PodiumPlayer: React.FC<{
  entry: LeaderboardEntry;
  isCenter: boolean;
  /**
   * Visual podium slot (1=center/gold, 2=left/silver, 3=right/bronze). Decoupled
   * from `entry.rank` so ties at rank 1 still staircase the avatars to match the
   * blocks beneath instead of stacking everyone at the gold elevation.
   */
  slot: 1 | 2 | 3;
  showCurrentUserVariant?: boolean;
  onPress?: (entry: LeaderboardEntry) => void;
}> = ({ entry, isCenter, slot, showCurrentUserVariant, onPress }) => {
  const theme = useTheme();
  const baseStyles = useMemo(() => getWorldCupLeaderboardStyles(theme), [theme]);
  const styles = useMemo(() => getPodiumStageStyles(theme), [theme]);

  const size = isCenter ? CENTER_AVATAR_SIZE : SIDE_AVATAR_SIZE;
  const haloSize = Math.round(size * 1.15);
  const glowStyle =
    slot === 1
      ? styles.glowHaloFirst
      : slot === 2
        ? styles.glowHaloSecond
        : styles.glowHaloThird;

  const isMe = showCurrentUserVariant && entry.isCurrentUser;
  // Self is pressable too — the parent's onPress decides what a self-tap does
  // (the global leaderboard opens the editable self profile). Views that don't
  // support self-tap simply pass a handler that no-ops for the current user.
  const pressable = Boolean(onPress) && Boolean(entry.userId);

  const handlePress = useCallback(() => {
    if (pressable) onPress?.(entry);
  }, [entry, onPress, pressable]);

  return (
    <Pressable
      onPress={pressable ? handlePress : undefined}
      disabled={!pressable}
      accessibilityRole={pressable ? 'button' : undefined}
      accessibilityLabel={pressable ? `Open ${entry.name}'s profile` : undefined}
      style={({ pressed }) => [
        styles.playerCol,
        slot === 2 && styles.playerColRank2,
        slot === 3 && styles.playerColRank3,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressable && pressed && styles.playerPressed,
      ]}
    >
      <View style={styles.playerNameRow}>
        <Text
          style={[styles.playerName, isMe && styles.playerNameMe]}
          numberOfLines={1}
        >
          {entry.name}
        </Text>
      </View>
      <View style={styles.playerPointsRow}>
        <Text style={styles.playerPoints}>{entry.points.toLocaleString()}</Text>
        <Text style={styles.ptsLabel}>pts</Text>
      </View>

      <View style={[styles.medallionWrap, { width: size, height: size, marginTop: 6 }]}>
        <View
          style={[
            styles.glowHalo,
            glowStyle,
            { width: haloSize, height: haloSize, left: (size - haloSize) / 2, top: (size - haloSize) / 2 + 4 },
          ]}
        />
        <LeaderboardUserAvatar
          entry={entry}
          variant={isCenter ? 'podiumLarge' : 'podiumSmall'}
          styles={baseStyles}
        />
      </View>
    </Pressable>
  );
};

const PodiumBlock: React.FC<{ rank: 1 | 2 | 3; height: number }> = ({ rank, height }) => {
  const theme = useTheme();
  const styles = useMemo(() => getPodiumStageStyles(theme), [theme]);
  const isFirst = rank === 1;
  const palette = isFirst ? PODIUM_BLOCK_COLORS.first : PODIUM_BLOCK_COLORS.rest;

  return (
    <View style={[styles.block, { height }]}>
      <View style={[styles.blockSide, isFirst ? styles.blockSideFirst : styles.blockSideRest]} />
      <View style={[styles.blockEdge, isFirst ? styles.blockEdgeFirst : styles.blockEdgeRest]} />
      <View style={[styles.blockFace, isFirst && styles.blockFaceFirstGlow]}>
        <LinearGradient
          colors={[palette.faceTop, palette.faceBottom]}
          style={styles.blockGradientFill}
          start={{ x: 0, y: 0 }}
          end={{ x: 0, y: 1 }}
        />
        <Text
          style={[
            styles.blockNumeral,
            isFirst ? styles.blockNumeralFirst : styles.blockNumeralRest,
            isFirst ? styles.blockNumeralColorFirst : styles.blockNumeralColorRest,
          ]}
        >
          {rank}
        </Text>
      </View>
    </View>
  );
};

export const PodiumStage: React.FC<PodiumStageProps> = ({
  top3,
  period = 'overall',
  onPeriodChange,
  showPeriodToggle = false,
  showCurrentUserVariant,
  onPressPlayer,
  rewards,
  testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getPodiumStageStyles(theme), [theme]);

  const ordered = useMemo(() => orderForDisplay(top3), [top3]);
  const [left, center, right] = ordered;

  const rewardMap = useMemo(() => {
    if (!rewards?.length) return null;
    return new Map(rewards.map((r) => [r.place, r]));
  }, [rewards]);

  return (
    <View style={styles.root} testID={testID}>
      {showPeriodToggle ? (
        <View
          style={styles.toggleRow}
          accessibilityRole="tablist"
          accessibilityLabel={PODIUM_COPY.PERIOD_A11Y}
        >
          {PODIUM_PERIOD_OPTIONS.map((option) => {
            const isActive = option.key === period;
            return (
              <Pressable
                key={option.key}
                onPress={() => onPeriodChange?.(option.key)}
                style={[
                  styles.togglePill,
                  isActive ? styles.togglePillActive : styles.togglePillInactive,
                ]}
                accessibilityRole="tab"
                accessibilityState={{ selected: isActive }}
                accessibilityLabel={option.label}
              >
                <Text
                  style={[
                    styles.toggleLabel,
                    isActive ? styles.toggleLabelActive : styles.toggleLabelInactive,
                  ]}
                >
                  {option.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      ) : null}

      <View style={styles.playersRow}>
        <View style={styles.playerSlot}>
          {left ? (
            <PodiumPlayer
              entry={left}
              isCenter={false}
              slot={2}
              showCurrentUserVariant={showCurrentUserVariant}
              onPress={onPressPlayer}
            />
          ) : null}
        </View>
        <View style={styles.playerSlot}>
          {center ? (
            <PodiumPlayer
              entry={center}
              isCenter
              slot={1}
              showCurrentUserVariant={showCurrentUserVariant}
              onPress={onPressPlayer}
            />
          ) : null}
        </View>
        <View style={styles.playerSlot}>
          {right ? (
            <PodiumPlayer
              entry={right}
              isCenter={false}
              slot={3}
              showCurrentUserVariant={showCurrentUserVariant}
              onPress={onPressPlayer}
            />
          ) : null}
        </View>
      </View>

      <View style={styles.blocksRow}>
        <View style={styles.blockCol}>
          <PodiumBlock rank={2} height={PODIUM_BLOCK_HEIGHTS.second} />
        </View>
        <View style={styles.blockCol}>
          <PodiumBlock rank={1} height={PODIUM_BLOCK_HEIGHTS.first} />
        </View>
        <View style={styles.blockCol}>
          <PodiumBlock rank={3} height={PODIUM_BLOCK_HEIGHTS.third} />
        </View>
      </View>

      {rewardMap ? (
        <View style={styles.prizeRow}>
          {([2, 1, 3] as const).map((place) => {
            const reward = rewardMap.get(place);
            if (!reward) return <View key={place} style={styles.prizeCol} />;
            return (
              <View key={place} style={styles.prizeCol}>
                {!!reward.imageUrl && (
                  <Image
                    source={{ uri: reward.imageUrl }}
                    style={styles.prizeImage}
                    resizeMode="cover"
                  />
                )}
                <Text style={styles.prizeLabel} numberOfLines={2}>
                  {reward.label}
                </Text>
              </View>
            );
          })}
        </View>
      ) : null}
    </View>
  );
};
